<template>
  <div
    :style="{
      '--theme-primary': parsedTheme.primary,
      '--theme-secondary': parsedTheme.secondary,
    }"
    :data-version="version"
    class="relative overflow-hidden font-sans antialiased text-gray-800 price-table"
  >
    <div v-if="tableData" class="overflow-auto">
      <table class="w-full mb-8 text-sm text-left border-collapse table-auto">
        <thead>
          <tr class="bg-gray-900 ">
            <th
              v-for="{ key, text, center } in tableData.headers"
              :key="key"
              scope="col"
              class="px-3 py-3 text-white capitalize whitespace-no-wrap align-bottom"
              :class="{ 'text-center': center }"
            >
              {{ text }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in tableData.rows" :key="index" scope="row" class="leading-none">
            <td
              v-for="{ key, component, center } in tableData.headers"
              :key="key"
              class="px-2 py-3 text-sm whitespace-no-wrap border-t-2 border-table-border "
              :class="{ 'text-center': center }"
            >
              <component :is="component" :value="row">
                <span
                  :class="{
                    'text-red-600': row[key].sign < 0,
                    'text-green-600': row[key].sign > 0,
                  }"
                  class="capitalize"
                  >{{ row[key].text }}</span
                >
              </component>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
// Components
import CoinsNameCell from '@/components/price-table/coins-name-cell'
import ForexNameCell from '@/components/price-table/forex-name-cell'
import OfferCtaCell from '@/components/price-table/offer-cta-cell'
// Mixins
import handleContext from '@/mixins/handle-context'
import handleResize from '@/mixins/handle-resize'
import handleTranslations from '@/mixins/handle-translations'
// Services
import { getCoinsLatestAll, getForexLatestAll } from '@/services/prices.service'
// Transformers
import { tableTransformer as tableTransformerCoins } from '@/transformers/coins'
import { tableTransformer as tableTransformerForex } from '@/transformers/forex'
// Utils
import { parseJson } from '@/utils/json'

export default {
  name: 'PriceTable',
  mixins: [handleResize, handleContext, handleTranslations],
  provide() {
    return {
      $offer: () => this.parsedOffer,
      $component: 'price-table',
      $visitLinkParams: () => this.visitLinkParams,
      $block: this.block || 'price-table',
    }
  },
  props: {
    ...handleContext.props,
    ...handleTranslations.props,
    /**
     * The type of currency to retrieve data for.
     * @values coins, forex
     */
    type: {
      type: String,
      required: true,
      validator(type) {
        return ['coins', 'forex'].includes(type)
      },
    },
    /**
     * The number of currencies we want to request from the API.
     */
    limit: {
      type: [Number, String],
      default: 10,
    },
    /**
     * A stringified array of objects that when parsed should
     * look like the following format:
        [{
          permalink: "https://themezilla.test/coins-coin-price/"
          slug: "bitcoin"
          symbol: "BTC"
        }]
     */
    pages: {
      type: String,
    },
    /**
     * A prefetched stringified JSON (from backend) to use as data for the component.
     * If blank, the data will be fetched on frontend instead.
     */
    priceData: {
      type: String,
    },
    /**
     * A JSON object for translations for the table headers.
     */
    translations: {
      type: String,
    },
    /**
     * The text that appears in the CTA under the 'Visit' column.
     */
    ctaText: {
      type: String,
      default: 'trade',
    },
    /**
     * The offer that the CTA will link towards
     */
    ctaOffer: {
      type: String,
    },
    /**
     * A stringified object of colours to use for the component. The values
     * should match the site that the component is placed on.
     * @args { primary, secondary }
     */
    theme: {
      type: String,
    },
    previewTemplate: {
      type: String,
      default: undefined,
    },
    block: {
      type: String,
    },
  },
  components: {
    CoinsNameCell,
    ForexNameCell,
    OfferCtaCell,
  },
  data() {
    return {
      data: undefined,
    }
  },
  computed: {
    parsedPages() {
      return this.pages ? parseJson(this.pages) : null
    },
    parsedTheme() {
      return this.theme ? parseJson(this.theme) : {}
    },
    parsedTranslations() {
      return this.translations ? parseJson(this.translations) : {}
    },
    parsedOffer() {
      return this.ctaOffer ? parseJson(this.ctaOffer) : null
    },
    showCta() {
      return !!this.parsedOffer
    },
    tableData() {
      if (!this.data || !this.data.length) return null

      const { minSm, minMd, minLg } = this.container
      /**
       * The table columns will appear in this order!
       *
       * key [required] - The key defined in the transformer.
       * text [required] - The text that appears in the table header cells.
       * component - The component to use in the column's cell.
       * show - The condition to show the column.
       * center - Align the cell in center
       */
      const headerMap = {
        coins: [
          { key: 'name', text: 'coin', component: 'CoinsNameCell', show: true },
          { key: 'symbol', text: 'symbol', show: minMd },
          { key: 'price', text: 'price', show: true },
          { key: 'change1h', text: '1h', show: minMd },
          { key: 'change24h', text: '24h', show: true },
          { key: 'change7d', text: '7d', show: minSm },
          { key: 'volume24h', text: '24-volume', show: minLg },
          { key: 'circulatingSupply', text: 'circulating-supply', show: minLg },
          { key: 'totalSupply', text: 'total-supply', show: minLg },
          { key: 'marketCapLong', text: 'market-cap', show: minLg },
          { key: 'offer', text: 'visit', component: 'OfferCtaCell', show: this.showCta, center: true },
        ],
        forex: [
          { key: 'name', text: 'pair', component: 'ForexNameCell', show: true },
          { key: 'bid', text: 'bid', show: true },
          { key: 'ask', text: 'ask', show: minSm },
          { key: 'high', text: 'high', show: minMd },
          { key: 'low', text: 'low', show: minMd },
          { key: 'change24h', text: '24-chg', show: true },
          { key: 'changePercent', text: 'chg', show: true },
        ],
      }

      const headers = headerMap[this.type]
        .filter(({ show }) => show)
        .map(({ key, text, component, center }) => ({
          component: component || 'div',
          center,
          key,
          text: this.$translate(text),
        }))

      return { headers, rows: this.data }
    },
    translatedCtaText() {
      return this.$translate(this.ctaText)
    },
    visitLinkParams() {
      return {
        previewTemplate: this.previewTemplate,
      }
    },
  },
  async created() {
    this.fetchTableData()
  },
  methods: {
    /**
     * Request data from the API for the table.
     */
    async fetchTableData() {
      const getDataLatestAll = this.type === 'coins' ? getCoinsLatestAll : getForexLatestAll
      const tableTransformer = this.type === 'coins' ? tableTransformerCoins : tableTransformerForex

      const parsedData = parseJson(this.priceData)
      const priceData =
        (parsedData && parsedData.slice(0, this.limit)) ||
        (await getDataLatestAll(this.priceService, { limit: this.limit }))

      this.data = priceData.map(item =>
        tableTransformer(
          {
            ...item,
            permalink: this.getPermalink(item.symbol),
            offer: { ctaText: this.translatedCtaText },
          },
          {
            currency: this.currency,
            language: this.language,
            country: this.country,
          },
          this.parsedTranslations
        )
      )
    },
    /**
     * Search the array of page objects for the page that
     * matches the symbol searched for and return the permalink.
     * @param {string} symbol The symbol to search parsedPages for.
     * @return {string} The permalink for the searched symbol.
     */
    getPermalink(symbol) {
      if (!this.parsedPages || !symbol) return null
      const foundPage = this.parsedPages.find(page => page.symbol === symbol)
      return foundPage ? foundPage.permalink : null
    },
  },
}
</script>

<style>
@tailwind base;
@tailwind components;
@tailwind utilities;
</style>
